import React, { useEffect, useState } from 'react';
import { postCategoryService } from '../../services/postCategoryService';
import { PostCategory } from '../../types';

const emptyForm = {
  name: '',
  description: '',
  icon: '',
  color: '',
  sort_order: 0,
};

const AdminPostCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCategory, setNewCategory] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState({ ...emptyForm });
  const [savingEdit, setSavingEdit] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await postCategoryService.list({ with_counts: true });
      setCategories(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Không thể tải danh mục bài viết');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.name.trim()) {
      setError('Tên danh mục không được để trống');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const { category, message } = await postCategoryService.create({
        name: newCategory.name.trim(),
        description: newCategory.description?.trim() || undefined,
        icon: newCategory.icon?.trim() || undefined,
        color: newCategory.color?.trim() || undefined,
        sort_order: Number.isFinite(newCategory.sort_order) ? Number(newCategory.sort_order) : 0,
      });
      setCategories((prev) => [category, ...prev].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      setNewCategory({ ...emptyForm });
      setSuccess(message || 'Đã tạo danh mục');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Không thể tạo danh mục');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (category: PostCategory) => {
    setEditingId(category.id);
    setEditingData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      color: category.color || '',
      sort_order: category.sort_order ?? 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingData({ ...emptyForm });
  };

  const saveEdit = async (categoryId: number) => {
    if (!editingData.name.trim()) {
      setError('Tên danh mục không được để trống');
      return;
    }
    setSavingEdit(true);
    setError('');
    try {
      const { category, message } = await postCategoryService.update(categoryId, {
        name: editingData.name.trim(),
        description: editingData.description?.trim() || undefined,
        icon: editingData.icon?.trim() || undefined,
        color: editingData.color?.trim() || undefined,
        sort_order: Number.isFinite(editingData.sort_order) ? Number(editingData.sort_order) : undefined,
      });
      setCategories((prev) => prev.map((cat) => (cat.id === categoryId ? category : cat)).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      setSuccess(message || 'Đã cập nhật danh mục');
      cancelEdit();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Không thể cập nhật danh mục');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteCategory = async (categoryId: number) => {
    if (!window.confirm('Bạn chắc chắn muốn xóa danh mục này?')) return;
    setError('');
    try {
      const { message } = await postCategoryService.remove(categoryId);
      setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
      setSuccess(message || 'Đã xóa danh mục');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Không thể xóa danh mục');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-xl border border-gray-200 p-4">
        <h1 className="text-2xl font-bold mb-1">Danh mục bài viết</h1>
        <p className="text-gray-500">Quản lý các danh mục hiển thị trong cộng đồng</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">{success}</div>
      )}

      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Thêm danh mục mới</h2>
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleCreate}>
          <label className="text-sm">
            <span className="text-gray-700">Tên danh mục *</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCategory.name}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Biểu tượng (emoji)</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCategory.icon}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, icon: e.target.value }))}
              placeholder="Ví dụ: 🔥"
              maxLength={4}
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Màu nền (tuỳ chọn)</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCategory.color}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, color: e.target.value }))}
              placeholder="#FEE2E2 hoặc gradient"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Thứ tự hiển thị</span>
            <input
              type="number"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCategory.sort_order}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, sort_order: Number(e.target.value) }))}
              min={0}
            />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-gray-700">Mô tả</span>
            <textarea
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              value={newCategory.description}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, description: e.target.value }))}
            />
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
              disabled={creating}
            >
              {creating ? 'Đang tạo...' : 'Tạo danh mục'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Danh sách danh mục</h2>
          {loading && <span className="text-sm text-gray-500">Đang tải...</span>}
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-gray-600 text-left">
                <th className="py-2 pr-4">Thứ tự</th>
                <th className="py-2 pr-4">Tên</th>
                <th className="py-2 pr-4">Slug</th>
                <th className="py-2 pr-4">Mô tả</th>
                <th className="py-2 pr-4">Bài viết</th>
                <th className="py-2 pr-4 text-right">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const isEditing = editingId === cat.id;
                return (
                  <tr key={cat.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4">
                      {isEditing ? (
                        <input
                          type="number"
                          className="border border-gray-300 rounded-lg px-2 py-1 w-20"
                          value={editingData.sort_order}
                          onChange={(e) => setEditingData((prev) => ({ ...prev, sort_order: Number(e.target.value) }))}
                        />
                      ) : (
                        cat.sort_order ?? '-'
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{cat.icon || '🏷️'}</span>
                        {isEditing ? (
                          <input
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.name}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, name: e.target.value }))}
                          />
                        ) : (
                          <div>
                            <div className="font-medium text-gray-900">{cat.name}</div>
                            {cat.color && (
                              <div className="text-xs text-gray-500">Màu: {cat.color}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{cat.slug}</td>
                    <td className="py-2 pr-4">
                      {isEditing ? (
                        <>
                          <textarea
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            rows={2}
                            value={editingData.description}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, description: e.target.value }))}
                          />
                          <input
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full mt-2"
                            placeholder="Biểu tượng"
                            value={editingData.icon}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, icon: e.target.value }))}
                            maxLength={4}
                          />
                          <input
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full mt-2"
                            placeholder="Màu"
                            value={editingData.color}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, color: e.target.value }))}
                          />
                        </>
                      ) : (
                        cat.description || '-'
                      )}
                    </td>
                    <td className="py-2 pr-4 text-center">{cat.posts_count ?? 0}</td>
                    <td className="py-2 pr-0 text-right space-x-2 whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveEdit(cat.id)}
                            className="px-3 py-1 rounded-lg bg-green-600 text-white"
                            disabled={savingEdit}
                          >
                            {savingEdit ? 'Đang lưu...' : 'Lưu'}
                          </button>
                          <button onClick={cancelEdit} className="px-3 py-1 rounded-lg bg-gray-200 text-gray-700">
                            Hủy
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(cat)}
                            className="px-3 py-1 rounded-lg bg-blue-600 text-white"
                          >
                            Sửa
                          </button>
                          <button
                            onClick={() => deleteCategory(cat.id)}
                            className="px-3 py-1 rounded-lg bg-red-600 text-white"
                          >
                            Xóa
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPostCategoriesPage;

